/**
 * FarmRoadWeatherPanel.jsx
 *
 * Panneau de visualisation des données météo et prévisions
 * Affiche:
 * - Météo actuelle
 * - Graphiques historiques (7-90 jours)
 * - Prévisions 7 jours
 * - Indices de risques agricoles (Botrytis, Oïdium)
 */

import React, { useState, useEffect } from 'react';
import {
  Cloud,
  CloudRain,
  Wind,
  Droplets,
  ThermometerSun,
  AlertTriangle,
  RefreshCw,
  Calendar,
  TrendingUp,
  TrendingDown,
  Info
} from 'lucide-react';
import { useMediaQuery, useTheme } from '@mui/material';
import { LineChart, Line, BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceArea } from 'recharts';
import FarmRoadWeatherService from './FarmRoadWeatherService';
import AgriculturalRiskCalculator from './AgriculturalRiskCalculator';
import FarmRoadThresholdsManager from './FarmRoadThresholdsManager';

const FarmRoadWeatherPanel = ({ farmId, farmName, farmDetail }) => {
  // Responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const [currentWeather, setCurrentWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [history, setHistory] = useState(null);

  const [selectedPeriod, setSelectedPeriod] = useState(7); // 7, 30, 90 jours
  const [selectedView, setSelectedView] = useState('current'); // 'current', 'forecast', 'history'

  // Paramètres de culture pour calcul d'irrigation
  const [cropCoefficient, setCropCoefficient] = useState(0.8); // Kc par défaut
  const [isCoveredCrop, setIsCoveredCrop] = useState(true); // Par défaut sous serre
  const [cultureName, setCultureName] = useState(null);

  /**
   * Obtient la période de prévision horaire avec titre de jour
   * Retourne "Samedi 8 mars" pour le jour concerné
   */
  const getHourlyForecastPeriod = () => {
    if (!forecast || !forecast.hourly || forecast.hourly.length === 0) return '';

    const now = new Date();
    const twentyFourHoursLater = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    const startDay = now.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    const endDay = twentyFourHoursLater.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });

    // Capitaliser la première lettre
    const capitalizedStart = startDay.charAt(0).toUpperCase() + startDay.slice(1);

    return capitalizedStart;
  };

  /**
   * Calcule les statistiques météo pour les prochaines 24h
   */
  const calculate24hStats = () => {
    if (!forecast || !forecast.hourly) return null;

    const now = new Date();
    const twentyFourHoursLater = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    const hourlyData = forecast.hourly.filter(hour => {
      const forecastTime = new Date(hour.forecast_time || hour.forecastTime);
      return forecastTime >= now && forecastTime <= twentyFourHoursLater;
    });

    if (hourlyData.length === 0) return null;

    // Calcul des statistiques météo de base
    const temperatures = hourlyData.map(h => h.temperature);
    const humidities = hourlyData.map(h => h.humidity);
    const windSpeeds = hourlyData.map(h => h.windSpeed || h.wind_speed || 0);
    const precipitations = hourlyData.map(h => h.precipitationAmount || h.precipitation_amount || 0);

    const tempMin = Math.min(...temperatures);
    const tempMax = Math.max(...temperatures);
    const avgHumidity = humidities.reduce((a, b) => a + b, 0) / humidities.length;
    const avgWindSpeed = windSpeeds.reduce((a, b) => a + b, 0) / windSpeeds.length;
    const maxWindSpeed = Math.max(...windSpeeds);
    const totalPrecipitation = precipitations.reduce((a, b) => a + b, 0);

    // Calcul du point de rosée moyen (formule simplifiée)
    const avgTemp = temperatures.reduce((a, b) => a + b, 0) / temperatures.length;
    const dewPoint = avgTemp - ((100 - avgHumidity) / 5);

    // Trouver l'heure du point de rosée le plus bas (moment optimal pour irrigation)
    let minDewPoint = Infinity;
    let dewPointTime = null;
    hourlyData.forEach(hour => {
      const temp = hour.temperature;
      const humidity = hour.humidity;
      const hourDewPoint = temp - ((100 - humidity) / 5);
      if (hourDewPoint < minDewPoint) {
        minDewPoint = hourDewPoint;
        dewPointTime = new Date(hour.forecast_time || hour.forecastTime);
      }
    });

    return {
      tempMin,
      tempMax,
      avgHumidity: Math.round(avgHumidity),
      dewPoint: Math.round(dewPoint * 10) / 10,
      dewPointTime: dewPointTime,
      minDewPoint: Math.round(minDewPoint * 10) / 10,
      avgWindSpeed: Math.round(avgWindSpeed * 10) / 10,
      maxWindSpeed: Math.round(maxWindSpeed * 10) / 10,
      totalPrecipitation: Math.round(totalPrecipitation * 10) / 10
    };
  };

  /**
   * Calcule les statistiques pour les 5 prochains jours (indicateurs irrigation)
   * Retourne : cumul précipitations, ET0 cumulée, VPD moyen, amplitude thermique
   */
  const calculate5DayStats = () => {
    if (!forecast || !forecast.daily) return null;

    const now = new Date();
    const fiveDaysLater = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);

    // Filtrer les 5 prochains jours (exclure aujourd'hui)
    const dailyData = forecast.daily.filter(day => {
      const forecastDate = new Date(day.forecast_time || day.forecastTime);
      return forecastDate > now && forecastDate <= fiveDaysLater;
    }).slice(0, 5); // Assurer exactement 5 jours

    if (dailyData.length === 0) return null;

    // 1. Cumul précipitations (mm)
    const totalPrecipitation = dailyData.reduce((sum, day) => {
      return sum + (day.precipitation_amount || day.precipitationAmount || 0);
    }, 0);

    // 2. ET0 cumulée (évapotranspiration) - Formule simplifiée Penman-Monteith
    const totalET0 = dailyData.reduce((sum, day) => {
      const temp = day.temperature || day.temp_max || 0;
      const humidity = day.humidity || 50;
      const windSpeed = day.wind_speed || day.windSpeed || 0;

      // Calcul VPD
      const saturationVP = 0.6108 * Math.exp((17.27 * temp) / (temp + 237.3));
      const actualVP = saturationVP * (humidity / 100);
      const vpd = saturationVP - actualVP;

      // ET0 estimation (mm/jour)
      const radiationEffect = Math.max(0, temp - 5) * 0.15;
      const windEffect = windSpeed * 0.3;
      const vpdEffect = vpd * 2.5;
      const et0 = radiationEffect + windEffect + vpdEffect;

      return sum + Math.max(0, et0);
    }, 0);

    // 3. VPD moyen (kPa) - Indicateur stress hydrique
    const avgVPD = dailyData.reduce((sum, day) => {
      const temp = day.temperature || day.temp_max || 0;
      const humidity = day.humidity || 50;
      const vpd = AgriculturalRiskCalculator.calculateVPD(temp, humidity);
      return sum + vpd;
    }, 0) / dailyData.length;

    // 4. Amplitude thermique (max - min sur 5 jours)
    let absoluteMin = Infinity;
    let absoluteMax = -Infinity;

    dailyData.forEach(day => {
      const tempMin = day.temp_min || day.tempMin || day.temperature;
      const tempMax = day.temp_max || day.tempMax || day.temperature;

      if (tempMin < absoluteMin) absoluteMin = tempMin;
      if (tempMax > absoluteMax) absoluteMax = tempMax;
    });

    const thermalAmplitude = absoluteMax - absoluteMin;

    return {
      totalPrecipitation: Math.round(totalPrecipitation * 10) / 10,
      totalET0: Math.round(totalET0 * 10) / 10,
      avgVPD: Math.round(avgVPD * 100) / 100,
      thermalAmplitude: Math.round(thermalAmplitude * 10) / 10,
      dayCount: dailyData.length // Pour vérifier qu'on a bien 5 jours
    };
  };

  /**
   * Fonction de couleur pour cumul précipitations (style pastel)
   * 0-5mm = vert, 5-20mm = bleu clair, 20-50mm = bleu, >50mm = bleu foncé
   */
  const getPrecipitationColor = (mm) => {
    if (mm <= 5) return { bg: 'from-green-50 to-green-100', border: 'border-green-200', title: 'text-green-700', value: 'text-green-900', subtitle: 'text-green-600', icon: 'text-green-500' };
    if (mm <= 20) return { bg: 'from-blue-50 to-blue-100', border: 'border-blue-200', title: 'text-blue-700', value: 'text-blue-900', subtitle: 'text-blue-600', icon: 'text-blue-500' };
    if (mm <= 50) return { bg: 'from-indigo-50 to-indigo-100', border: 'border-indigo-200', title: 'text-indigo-700', value: 'text-indigo-900', subtitle: 'text-indigo-600', icon: 'text-indigo-500' };
    return { bg: 'from-purple-50 to-purple-100', border: 'border-purple-200', title: 'text-purple-700', value: 'text-purple-900', subtitle: 'text-purple-600', icon: 'text-purple-500' };
  };

  /**
   * Fonction de couleur pour ET0 cumulée (style pastel)
   * <10mm = vert (faible besoin), 10-25mm = jaune (modéré), 25-40mm = orange (élevé), >40mm = rouge (très élevé)
   */
  const getET0Color = (et0) => {
    if (et0 < 10) return { bg: 'from-green-50 to-green-100', border: 'border-green-200', title: 'text-green-700', value: 'text-green-900', subtitle: 'text-green-600', icon: 'text-green-500' };
    if (et0 < 25) return { bg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-200', title: 'text-yellow-700', value: 'text-yellow-900', subtitle: 'text-yellow-600', icon: 'text-yellow-500' };
    if (et0 < 40) return { bg: 'from-orange-50 to-orange-100', border: 'border-orange-200', title: 'text-orange-700', value: 'text-orange-900', subtitle: 'text-orange-600', icon: 'text-orange-500' };
    return { bg: 'from-red-50 to-red-100', border: 'border-red-200', title: 'text-red-700', value: 'text-red-900', subtitle: 'text-red-600', icon: 'text-red-500' };
  };

  /**
   * Fonction de couleur pour VPD moyen (style pastel)
   * 0.8-1.2 kPa = vert (optimal), 0.4-0.8 ou 1.2-2.0 = jaune (acceptable), <0.4 ou >2.0 = rouge (stress)
   */
  const getVPDColor = (vpd) => {
    if (vpd >= 0.8 && vpd <= 1.2) return { bg: 'from-green-50 to-green-100', border: 'border-green-200', title: 'text-green-700', value: 'text-green-900', subtitle: 'text-green-600', icon: 'text-green-500' };
    if ((vpd >= 0.4 && vpd < 0.8) || (vpd > 1.2 && vpd <= 2.0)) return { bg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-200', title: 'text-yellow-700', value: 'text-yellow-900', subtitle: 'text-yellow-600', icon: 'text-yellow-500' };
    return { bg: 'from-red-50 to-red-100', border: 'border-red-200', title: 'text-red-700', value: 'text-red-900', subtitle: 'text-red-600', icon: 'text-red-500' };
  };

  /**
   * Fonction de couleur pour amplitude thermique (style pastel)
   * <15°C = vert (stable), 15-20°C = jaune (modéré), 20-25°C = orange (élevé), >25°C = rouge (extrême)
   */
  const getAmplitudeColor = (amplitude) => {
    if (amplitude < 15) return { bg: 'from-green-50 to-green-100', border: 'border-green-200', title: 'text-green-700', value: 'text-green-900', subtitle: 'text-green-600', icon: 'text-green-500' };
    if (amplitude < 20) return { bg: 'from-yellow-50 to-yellow-100', border: 'border-yellow-200', title: 'text-yellow-700', value: 'text-yellow-900', subtitle: 'text-yellow-600', icon: 'text-yellow-500' };
    if (amplitude < 25) return { bg: 'from-orange-50 to-orange-100', border: 'border-orange-200', title: 'text-orange-700', value: 'text-orange-900', subtitle: 'text-orange-600', icon: 'text-orange-500' };
    return { bg: 'from-red-50 to-red-100', border: 'border-red-200', title: 'text-red-700', value: 'text-red-900', subtitle: 'text-red-600', icon: 'text-red-500' };
  };

  /**
   * Calcule l'évapotranspiration horaire (ET0) en mm/heure
   * Utilise la formule simplifiée de Penman-Monteith
   * @param {number} temp - Température en °C
   * @param {number} humidity - Humidité relative en %
   * @param {number} windSpeed - Vitesse du vent en m/s
   * @returns {number} ET0 en mm/heure
   */
  const calculateHourlyET0 = (temp, humidity, windSpeed) => {
    // Calcul du VPD (Vapor Pressure Deficit)
    const saturationVP = 0.6108 * Math.exp((17.27 * temp) / (temp + 237.3));
    const actualVP = saturationVP * (humidity / 100);
    const vpd = saturationVP - actualVP;

    // Composantes de l'ET0 (en mm/jour)
    const radiationEffect = Math.max(0, temp - 5) * 0.15;
    const windEffect = windSpeed * 0.3;
    const vpdEffect = vpd * 2.5;

    // ET0 journalier (mm/jour)
    const et0Daily = radiationEffect + windEffect + vpdEffect;

    // Conversion en ET0 horaire (mm/heure)
    const et0Hourly = et0Daily / 24;

    return Math.max(0, et0Hourly); // Ne peut pas être négatif
  };

  /**
   * Fonction de couleur pour les points ET0 horaire
   * Zones: 0.10-0.20 (optimal), 0.20-0.30 (modéré), 0.30-0.40 (élevé), >0.40 (critique)
   */
  const getET0DotColor = (et0Hourly) => {
    if (et0Hourly <= 0.20) return '#10B981'; // Vert (optimal)
    if (et0Hourly <= 0.30) return '#EAB308'; // Jaune (modéré)
    if (et0Hourly <= 0.40) return '#F97316'; // Orange (élevé)
    return '#EF4444'; // Rouge (critique)
  };

  /**
   * Calcule le facteur d'ajustement VPD pour l'irrigation
   * @param {number} vpd - VPD en kPa
   * @returns {number} Facteur multiplicateur (0.7 à 1.4)
   */
  const getVPDFactor = (vpd) => {
    if (vpd < 0.4) return 0.7;  // Réduire irrigation - Risque fongique
    if (vpd < 0.8) return 0.9;  // Légèrement réduire
    if (vpd <= 1.2) return 1.0; // Optimal
    if (vpd <= 2.0) return 1.2; // Augmenter - Stress modéré
    return 1.4;                 // Irrigation maximale - Stress critique
  };

  /**
   * Calcule la recommandation d'irrigation horaire
   * @param {number} et0 - Evapotranspiration de référence (mm/h)
   * @param {number} vpd - VPD (kPa)
   * @param {number} rain - Précipitations prévues (mm/h)
   * @param {number} Kc - Coefficient cultural
   * @param {boolean} isCoveredCrop - Culture sous serre/tunnel
   * @returns {number} Irrigation recommandée en mm/h
   */
  const calculateIrrigationRecommendation = (et0, vpd, rain, Kc, isCoveredCrop) => {
    const vpdFactor = getVPDFactor(vpd);
    const irrigationNeed = et0 * Kc * vpdFactor;

    // Si sous serre, on ne déduit pas la pluie
    const effectiveRain = isCoveredCrop ? 0 : rain;

    // Efficience système goutte-à-goutte : 92%
    const irrigation = (irrigationNeed - effectiveRain) / 0.92;

    return Math.max(0, irrigation); // Jamais négatif
  };

  /**
   * Fonction de couleur pour les points d'irrigation
   * Zones: 0-0.15 (faible), 0.15-0.25 (modérée), 0.25-0.35 (importante), >0.35 (maximale)
   */
  const getIrrigationDotColor = (irrigation) => {
    if (irrigation <= 0.15) return '#10B981'; // Vert (faible)
    if (irrigation <= 0.25) return '#EAB308'; // Jaune (modérée)
    if (irrigation <= 0.35) return '#F97316'; // Orange (importante)
    return '#EF4444'; // Rouge (maximale)
  };

  /**
   * Chargement initial des données
   */
  useEffect(() => {
    if (farmId) {
      loadWeatherData();
    }
  }, [farmId]);

  /**
   * Recharge l'historique quand la période change
   */
  useEffect(() => {
    if (farmId && selectedView === 'history') {
      loadHistoryData();
    }
  }, [selectedPeriod, selectedView]);

  /**
   * Charge toutes les données météo
   */
  const loadWeatherData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Charger en parallèle météo + paramètres culture
      const [current, forecastData] = await Promise.all([
        FarmRoadWeatherService.getWeatherForLocation({ farmId }),
        FarmRoadWeatherService.getWeatherForecast({ farmId }),
        loadCropSettings() // Charger les paramètres de culture
      ]);

      setCurrentWeather(current);
      setForecast(forecastData);

      // Charger l'historique si nécessaire
      if (selectedView === 'history') {
        await loadHistoryData();
      }
    } catch (err) {
      console.error('Error loading weather data:', err);
      setError(err.message || 'Erreur lors du chargement des données météo');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Charge les données historiques
   */
  const loadHistoryData = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - selectedPeriod);

      // Formater les dates en YYYY-MM-DD pour la requête
      const formatDate = (date) => date.toISOString().split('T')[0];

      console.log(`[FarmRoadWeatherPanel] Chargement historique ${selectedPeriod} jours pour ferme ${farmId}`);
      console.log(`  Période: ${formatDate(startDate)} → ${formatDate(endDate)}`);

      const historyData = await FarmRoadWeatherService.getWeatherHistory({
        farmId,
        startTime: formatDate(startDate),  // Format YYYY-MM-DD
        endTime: formatDate(endDate),      // Format YYYY-MM-DD
        limit: selectedPeriod * 24 // Max points
      });

      console.log(`[FarmRoadWeatherPanel] Historique chargé: ${historyData?.length || 0} enregistrements`);
      setHistory(historyData);
    } catch (err) {
      console.error('Error loading weather history:', err);
      setHistory([]); // Afficher tableau vide en cas d'erreur
    }
  };

  /**
   * Charge les paramètres de culture pour les calculs d'irrigation
   */
  const loadCropSettings = async () => {
    try {
      // FarmRoadThresholdsManager est un singleton, pas besoin de 'new'
      const config = await FarmRoadThresholdsManager.getThresholds(farmId);

      if (config && config.culture) {
        const Kc = FarmRoadThresholdsManager.getCropCoefficient(config.culture);
        const covered = config.isCoveredCrop !== undefined ? config.isCoveredCrop : true;

        setCropCoefficient(Kc);
        setIsCoveredCrop(covered);
        setCultureName(config.culture);

        console.log(`[FarmRoadWeatherPanel] Paramètres culture chargés: ${config.culture}, Kc=${Kc}, Serre=${covered}`);
      } else {
        console.warn('[FarmRoadWeatherPanel] Culture non configurée, utilisation valeurs par défaut');
      }
    } catch (err) {
      console.error('Error loading crop settings:', err);
      // Garder les valeurs par défaut
    }
  };

  /**
   * Rafraîchit les données météo
   */
  const handleRefresh = async () => {
    setRefreshing(true);

    try {
      await FarmRoadWeatherService.refreshWeatherData(farmId);
      await loadWeatherData();
    } catch (err) {
      console.error('Error refreshing weather:', err);
      setError('Erreur lors du rafraîchissement');
    } finally {
      setRefreshing(false);
    }
  };

  /**
   * Formatte la température
   */
  const formatTemp = (temp) => {
    if (temp === null || temp === undefined) return 'N/A';
    return `${Math.round(temp)}°C`;
  };

  /**
   * Formatte la vitesse du vent
   */
  const formatWind = (speed) => {
    if (speed === null || speed === undefined) return 'N/A';
    return `${Math.round(speed * 3.6)} km/h`;
  };

  /**
   * Retourne la couleur pour un niveau de risque
   */
  const getRiskColor = (risk) => {
    if (risk === null || risk === undefined) return 'text-gray-400';
    if (risk < 25) return 'text-green-500';
    if (risk < 50) return 'text-yellow-500';
    if (risk < 75) return 'text-orange-500';
    return 'text-red-600';
  };

  /**
   * Retourne le label pour un risque
   */
  const getRiskLabel = (risk) => {
    if (risk === null || risk === undefined) return 'Inconnu';
    if (risk < 25) return 'Faible';
    if (risk < 50) return 'Modéré';
    if (risk < 75) return 'Élevé';
    return 'Critique';
  };

  /**
   * Prépare les données pour les graphiques historiques
   * Utilise les données quotidiennes agrégées depuis weather_history_daily
   */
  const prepareHistoryChartData = () => {
    if (!history || !Array.isArray(history)) return [];

    return history.map(item => {
      // item.date est une date Date object ou string depuis /history/daily
      const date = new Date(item.date);

      return {
        time: date.toLocaleDateString('fr-FR', {
          month: 'short',
          day: 'numeric'
        }),
        // Utiliser les moyennes quotidiennes
        température: item.temperatures?.avg || 0,
        humidité: item.humidity?.avg || 0,
        précipitations: item.precipitation?.total || 0
      };
    });
  };

  /**
   * Prépare les données pour les prévisions
   */
  const prepareForecastData = () => {
    if (!forecast || !forecast.daily) return [];

    // Obtenir la date du jour à minuit
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Filtrer pour exclure la journée en cours et prendre les 5 prochains jours
    return forecast.daily
      .filter(day => {
        const forecastDate = new Date(day.forecast_time || day.forecastTime);
        forecastDate.setHours(0, 0, 0, 0);
        return forecastDate > today; // Uniquement les jours futurs
      })
      .slice(0, 5)
      .map(day => ({
        jour: new Date(day.forecast_time || day.forecastTime).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }),
        tempMin: day.temp_min || day.tempMin,
        tempMax: day.temp_max || day.tempMax,
        humidité: day.humidity,
        vent: day.windSpeed || day.wind_speed,
        pluie: day.precipitationAmount || day.precipitation_amount || 0,
        précipProba: day.precipitation_probability || day.precipitationProbability || 0,
        icône: day.weather_icon || day.icon
      }));
  };

  /**
   * Prépare les données pour les prévisions horaires (24h)
   */
  const prepareHourlyForecastData = () => {
    if (!forecast || !forecast.hourly) return [];

    // Filtrer uniquement les 24 prochaines heures
    const now = new Date();
    const twentyFourHoursLater = new Date(now.getTime() + 24 * 60 * 60 * 1000);

    return forecast.hourly
      .filter(hour => {
        const forecastTime = new Date(hour.forecast_time || hour.forecastTime);
        return forecastTime >= now && forecastTime <= twentyFourHoursLater;
      })
      .map(hour => {
        // Calculer VPD pour cette heure
        const vpd = AgriculturalRiskCalculator.calculateVPD(hour.temperature, hour.humidity);

        // Calculer ET0 horaire (mm/heure)
        const windSpeed = hour.windSpeed || hour.wind_speed || 0;
        const et0 = calculateHourlyET0(hour.temperature, hour.humidity, windSpeed);

        // Calculer irrigation recommandée (mm/heure)
        const rain = hour.precipitationAmount || hour.precipitation_amount || 0;
        const irrigation = calculateIrrigationRecommendation(et0, vpd, rain, cropCoefficient, isCoveredCrop);

        return {
          heure: new Date(hour.forecast_time || hour.forecastTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
          température: hour.temperature,
          humidité: hour.humidity,
          vent: windSpeed,
          pluie: rain,
          vpd: Math.round(vpd * 100) / 100, // Arrondi à 2 décimales
          et0: Math.round(et0 * 1000) / 1000, // Arrondi à 3 décimales (mm/h)
          irrigation: Math.round(irrigation * 1000) / 1000, // Arrondi à 3 décimales (mm/h)
          icône: hour.weather_icon || hour.icon
        };
      });
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
        <span className="ml-3 text-gray-600">Chargement des données météo...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6">
        <div className="flex items-start">
          <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5" />
          <div className="ml-3">
            <h3 className="text-red-800 font-semibold">Erreur</h3>
            <p className="text-red-700 mt-1">{error}</p>
            <button
              onClick={loadWeatherData}
              className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
            >
              Réessayer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête avec titre et bouton rafraîchir */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            <Cloud className="w-7 h-7 mr-2 text-blue-500" />
            {isMobile ? 'Météo de la ferme' : `Météo - ${farmName || `Ferme ${farmId}`}`}
          </h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            {isMobile ? 'Données et prévisions' : 'Données actuelles et prévisions pour votre exploitation'}
          </p>
        </div>

        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Actualisation...' : 'Actualiser'}
        </button>
      </div>

      {/* Onglets de navigation */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setSelectedView('current')}
          className={`px-4 sm:px-6 py-3 font-medium transition text-sm sm:text-base ${
            selectedView === 'current'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Actuel
        </button>
        <button
          onClick={() => setSelectedView('hourly')}
          className={`px-4 sm:px-6 py-3 font-medium transition text-sm sm:text-base ${
            selectedView === 'hourly'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {isMobile ? '24h' : 'Prévisions 24h'}
        </button>
        <button
          onClick={() => setSelectedView('forecast')}
          className={`px-4 sm:px-6 py-3 font-medium transition text-sm sm:text-base ${
            selectedView === 'forecast'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          {isMobile ? '5j' : 'Prévisions 5j'}
        </button>
        <button
          onClick={() => setSelectedView('history')}
          className={`px-4 sm:px-6 py-3 font-medium transition text-sm sm:text-base ${
            selectedView === 'history'
              ? 'border-b-2 border-blue-600 text-blue-600'
              : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          Historique
        </button>
      </div>

      {/* Vue Actuelle */}
      {selectedView === 'current' && currentWeather && (
        <div className="space-y-6">
          {/* Météo actuelle - Cartes principales */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Température */}
            <div className="bg-gradient-to-br from-red-50 to-rose-100 rounded-lg p-6 border border-red-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-red-700 text-sm font-medium">Température</p>
                  <p className="text-3xl font-bold text-red-900 mt-2">
                    {formatTemp(currentWeather.temperature)}
                  </p>
                  <p className="text-rose-600 text-sm mt-1">
                    Ressenti: {formatTemp(currentWeather.feels_like)}
                  </p>
                </div>
                <ThermometerSun className="w-8 h-8 text-red-500" />
              </div>
            </div>

            {/* Humidité */}
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200 relative group">
              <div className="flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-blue-700 text-sm font-medium">Humidité</p>
                    <Info className="w-4 h-4 text-blue-700 cursor-help" />
                  </div>
                  <p className="text-3xl font-bold text-blue-900 mt-2">
                    {currentWeather.humidity}%
                  </p>
                  <p className="text-blue-600 text-sm mt-1">
                    Point de rosée: {formatTemp(currentWeather.dew_point)}
                  </p>
                </div>
                <Droplets className="w-8 h-8 text-blue-500" />
              </div>
              {/* Tooltip */}
              <div className="absolute left-0 top-full mt-2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 shadow-lg">
                <p className="font-semibold mb-1">Humidité et Point de rosée</p>
                <p><strong>Humidité relative :</strong> Pourcentage de vapeur d'eau dans l'air par rapport au maximum possible à cette température.</p>
                <p className="mt-2"><strong>Point de rosée :</strong> Température à laquelle l'air devient saturé et la condensation commence. Plus le point de rosée est élevé, plus l'air est humide.</p>
              </div>
            </div>

            {/* Vent */}
            <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-6 border border-cyan-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-cyan-700 text-sm font-medium">Vent</p>
                  <p className="text-3xl font-bold text-cyan-900 mt-2">
                    {formatWind(currentWeather.wind_speed)}
                  </p>
                  <p className="text-cyan-600 text-sm mt-1">
                    Rafales: {formatWind(currentWeather.wind_gust || currentWeather.wind_speed)}
                  </p>
                </div>
                <Wind className="w-8 h-8 text-cyan-500" />
              </div>
            </div>

            {/* Précipitations */}
            <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-6 border border-indigo-200">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-indigo-700 text-sm font-medium">Précipitations</p>
                  <p className="text-3xl font-bold text-indigo-900 mt-2">
                    {currentWeather.precipitation_amount || 0} mm
                  </p>
                  <p className="text-indigo-600 text-sm mt-1">
                    {currentWeather.weather_description || 'Pas de pluie'}
                  </p>
                </div>
                <CloudRain className="w-8 h-8 text-indigo-500" />
              </div>
            </div>
          </div>

          {/* Indices agricoles */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <AlertTriangle className="w-5 h-5 mr-2 text-amber-500" />
              Indices de Risques Agricoles
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Risque Botrytis */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Risque Botrytis (Moisissure grise)</span>
                  <span className={`font-bold ${getRiskColor(currentWeather.botrytis_risk)}`}>
                    {getRiskLabel(currentWeather.botrytis_risk)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      currentWeather.botrytis_risk >= 75
                        ? 'bg-red-600'
                        : currentWeather.botrytis_risk >= 50
                        ? 'bg-orange-500'
                        : currentWeather.botrytis_risk >= 25
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${currentWeather.botrytis_risk || 0}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Conditions favorables: 15-25°C et humidité &gt; 85%
                </p>
              </div>

              {/* Risque Oïdium */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Risque Oïdium (Blanc)</span>
                  <span className={`font-bold ${getRiskColor(currentWeather.powdery_mildew_risk)}`}>
                    {getRiskLabel(currentWeather.powdery_mildew_risk)}
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className={`h-3 rounded-full transition-all ${
                      currentWeather.powdery_mildew_risk >= 75
                        ? 'bg-red-600'
                        : currentWeather.powdery_mildew_risk >= 50
                        ? 'bg-orange-500'
                        : currentWeather.powdery_mildew_risk >= 25
                        ? 'bg-yellow-500'
                        : 'bg-green-500'
                    }`}
                    style={{ width: `${currentWeather.powdery_mildew_risk || 0}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600">
                  Conditions favorables: 20-30°C et humidité 40-70%
                </p>
              </div>

              {/* Évapotranspiration */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Évapotranspiration (ET0)</span>
                  <span className="text-blue-600 font-bold">
                    {currentWeather.evapotranspiration?.toFixed(1) || 'N/A'} mm/jour
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Estimation des besoins en irrigation
                </p>
              </div>

              {/* Stress éolien */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">Stress Éolien</span>
                  <span className="text-cyan-600 font-bold">
                    {currentWeather.wind_stress || 'Faible'}
                  </span>
                </div>
                <p className="text-sm text-gray-600">
                  Impact du vent sur les cultures
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vue Prévisions */}
      {selectedView === 'forecast' && forecast && (() => {
        const stats = calculate5DayStats();
        return (
        <div className="space-y-6">
          {/* Indicateurs irrigation 5 jours - Cartes */}
          {stats && (() => {
            const precipColors = getPrecipitationColor(stats.totalPrecipitation);
            const et0Colors = getET0Color(stats.totalET0);
            const vpdColors = getVPDColor(stats.avgVPD);
            const ampColors = getAmplitudeColor(stats.thermalAmplitude);

            return (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* 1. Cumul Précipitations */}
              <div className={`bg-gradient-to-br ${precipColors.bg} rounded-lg p-6 border ${precipColors.border}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`${precipColors.title} text-sm font-medium`}>Précipitations (5j)</p>
                    <p className={`text-3xl font-bold ${precipColors.value} mt-2`}>
                      {stats.totalPrecipitation} mm
                    </p>
                    <p className={`${precipColors.subtitle} text-sm mt-1`}>
                      {stats.totalPrecipitation > 10
                        ? 'Reporter irrigation'
                        : stats.totalPrecipitation > 5
                        ? 'Surveiller'
                        : 'Irrigation nécessaire'}
                    </p>
                  </div>
                  <CloudRain className={`w-8 h-8 ${precipColors.icon}`} />
                </div>
              </div>

              {/* 2. ET0 cumulée - Plus important pour irrigation */}
              <div className={`bg-gradient-to-br ${et0Colors.bg} rounded-lg p-6 border ${et0Colors.border} relative group`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-1">
                      <p className={`${et0Colors.title} text-sm font-medium`}>ET0 cumulée (5j)</p>
                      <Info className={`w-4 h-4 ${et0Colors.title} cursor-help`} />
                    </div>
                    <p className={`text-3xl font-bold ${et0Colors.value} mt-2`}>
                      {stats.totalET0} mm
                    </p>
                    <p className={`${et0Colors.subtitle} text-sm mt-1`}>
                      {stats.totalET0 > 40
                        ? 'Besoin hydrique élevé'
                        : stats.totalET0 > 25
                        ? 'Besoin hydrique modéré'
                        : 'Besoin hydrique faible'}
                    </p>
                  </div>
                  <Droplets className={`w-8 h-8 ${et0Colors.icon}`} />
                </div>
                {/* Tooltip */}
                <div className="absolute left-0 top-full mt-2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 shadow-lg">
                  <p className="font-semibold mb-1">Évapotranspiration de référence (ET0)</p>
                  <p>Quantité d'eau évaporée du sol et transpirée par les plantes. Indique les besoins en irrigation : plus ET0 est élevé, plus il faut irriguer.</p>
                </div>
              </div>

              {/* 3. VPD moyen - Stress hydrique */}
              <div className={`bg-gradient-to-br ${vpdColors.bg} rounded-lg p-6 border ${vpdColors.border} relative group`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="flex items-center gap-1">
                      <p className={`${vpdColors.title} text-sm font-medium`}>VPD moyen (5j)</p>
                      <Info className={`w-4 h-4 ${vpdColors.title} cursor-help`} />
                    </div>
                    <p className={`text-3xl font-bold ${vpdColors.value} mt-2`}>
                      {stats.avgVPD} kPa
                    </p>
                    <p className={`${vpdColors.subtitle} text-sm mt-1`}>
                      {stats.avgVPD >= 0.8 && stats.avgVPD <= 1.2
                        ? 'Optimal'
                        : stats.avgVPD > 2.0
                        ? 'Stress hydrique élevé'
                        : stats.avgVPD < 0.4
                        ? 'Risque fongique'
                        : 'Acceptable'}
                    </p>
                  </div>
                  <TrendingUp className={`w-8 h-8 ${vpdColors.icon}`} />
                </div>
                {/* Tooltip */}
                <div className="absolute left-0 top-full mt-2 w-72 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 shadow-lg">
                  <p className="font-semibold mb-1">Déficit de Pression de Vapeur (VPD)</p>
                  <p>Différence entre l'humidité de l'air et celle dans les feuilles. Optimal : 0.8-1.2 kPa. VPD bas (&lt;0.4) favorise les maladies. VPD élevé (&gt;2.0) cause un stress hydrique.</p>
                </div>
              </div>

              {/* 4. Amplitude thermique - Gestion climat serre */}
              <div className={`bg-gradient-to-br ${ampColors.bg} rounded-lg p-6 border ${ampColors.border}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className={`${ampColors.title} text-sm font-medium`}>Amplitude thermique</p>
                    <p className={`text-3xl font-bold ${ampColors.value} mt-2`}>
                      {stats.thermalAmplitude}°C
                    </p>
                    <p className={`${ampColors.subtitle} text-sm mt-1`}>
                      {stats.thermalAmplitude > 25
                        ? 'Variation extrême'
                        : stats.thermalAmplitude > 20
                        ? 'Variation élevée'
                        : stats.thermalAmplitude > 15
                        ? 'Variation modérée'
                        : 'Climat stable'}
                    </p>
                  </div>
                  <ThermometerSun className={`w-8 h-8 ${ampColors.icon}`} />
                </div>
              </div>
            </div>
            );
          })()}

          {/* Graphiques des prévisions */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Graphique Températures */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Températures
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={prepareForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="jour" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="tempMax" stroke="#EF4444" strokeWidth={2} name="Max (°C)" dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="tempMin" stroke="#3B82F6" strokeWidth={2} name="Min (°C)" dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Graphique Humidité */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Humidité
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={prepareForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="jour" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="humidité" stroke="#3B82F6" strokeWidth={2} name="Humidité (%)" dot={{ r: 4 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Graphique Vent */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Vent
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={prepareForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="jour" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="vent" fill="#06B6D4" name="Vent (m/s)" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Graphique Précipitations */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Précipitations
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={prepareForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="jour" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="pluie" fill="#6366F1" name="Pluie (mm)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
        );
      })()}

      {/* Vue Prévisions 24h */}
      {selectedView === 'hourly' && forecast && forecast.hourly && (
        <div className="space-y-6">
          <h3 className="text-xl font-semibold text-gray-900">
            {getHourlyForecastPeriod()}
          </h3>

          {/* Cartes indicateurs clés */}
          {(() => {
            const stats = calculate24hStats();
            if (!stats) return null;

            return (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Carte Température */}
                <div className="bg-gradient-to-br from-red-50 to-rose-100 rounded-lg p-6 border border-red-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-red-700 text-sm font-medium">Températures</p>
                      <div className="flex items-baseline gap-2 mt-2">
                        <span className="text-2xl font-bold text-red-900">
                          {formatTemp(stats.tempMin)}
                        </span>
                        <span className="text-gray-500">→</span>
                        <span className="text-2xl font-bold text-red-600">
                          {formatTemp(stats.tempMax)}
                        </span>
                      </div>
                      <p className="text-rose-600 text-sm mt-1">Min → Max sur 24h</p>
                    </div>
                    <ThermometerSun className="w-8 h-8 text-red-500" />
                  </div>
                </div>

                {/* Carte Humidité */}
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border border-blue-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-blue-700 text-sm font-medium">Humidité moyenne</p>
                      <p className="text-4xl font-bold text-blue-900 mt-2">
                        {stats.avgHumidity}%
                      </p>
                      <p className="text-blue-600 text-sm mt-1">
                        Point de rosée de {formatTemp(stats.minDewPoint)} à {stats.dewPointTime ? new Date(stats.dewPointTime).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                      </p>
                    </div>
                    <Droplets className="w-8 h-8 text-blue-500" />
                  </div>
                </div>

                {/* Carte Vent */}
                <div className="bg-gradient-to-br from-cyan-50 to-cyan-100 rounded-lg p-6 border border-cyan-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-cyan-700 text-sm font-medium">Vent</p>
                      <p className="text-4xl font-bold text-cyan-900 mt-2">
                        {formatWind(stats.avgWindSpeed)}
                      </p>
                      <p className="text-cyan-600 text-sm mt-1">
                        Max: {formatWind(stats.maxWindSpeed)}
                      </p>
                    </div>
                    <Wind className="w-8 h-8 text-cyan-500" />
                  </div>
                </div>

                {/* Carte Précipitations */}
                <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 rounded-lg p-6 border border-indigo-200">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-indigo-700 text-sm font-medium">Précipitations</p>
                      <p className="text-4xl font-bold text-indigo-900 mt-2">
                        {stats.totalPrecipitation} mm
                      </p>
                      <p className="text-indigo-600 text-sm mt-1">Total sur 24h</p>
                    </div>
                    <CloudRain className="w-8 h-8 text-indigo-500" />
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Graphiques horaires */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Température horaire */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Température
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={prepareHourlyForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="heure" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="température" stroke="#EF4444" strokeWidth={2} name="Temp (°C)" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Humidité horaire */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Humidité
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={prepareHourlyForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="heure" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Line type="monotone" dataKey="humidité" stroke="#3B82F6" strokeWidth={2} name="Humidité (%)" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
            </div>

            {/* Vent horaire */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Vent
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={prepareHourlyForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="heure" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="vent" fill="#06B6D4" name="Vent (m/s)" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* Précipitations horaires */}
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Précipitations
              </h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={prepareHourlyForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="heure" angle={-45} textAnchor="end" height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="pluie" fill="#6366F1" name="Pluie (mm)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* VPD horaire - Graphique large pour optimisation irrigation */}
          <div className="mt-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  VPD (Déficit de Pression de Vapeur)
                </h3>
                <div className="relative group">
                  <Info className="w-4 h-4 text-gray-500 cursor-help" />
                  <div className="absolute left-0 top-full mt-2 w-80 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 shadow-lg">
                    <p className="font-semibold mb-1">Optimisation de l'irrigation</p>
                    <p>Ce graphique aide à choisir le meilleur moment pour irriguer. VPD optimal : 0.8-1.2 kPa (zone verte). Évitez d'irriguer quand VPD &gt; 2.0 kPa (stress hydrique) ou VPD &lt; 0.4 kPa (risque fongique).</p>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={prepareHourlyForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="heure" angle={-45} textAnchor="end" height={80} />
                  <YAxis
                    label={{ value: 'VPD (kPa)', angle: -90, position: 'insideLeft' }}
                    domain={[0, 'auto']}
                  />
                  <Tooltip
                    formatter={(value) => {
                      let status = '';
                      if (value >= 0.8 && value <= 1.2) status = ' (Optimal)';
                      else if (value < 0.4) status = ' (Critique - Risque fongique)';
                      else if (value > 2.0) status = ' (Critique - Stress hydrique)';
                      else status = ' (Acceptable)';
                      return [`${value} kPa${status}`, 'VPD'];
                    }}
                    labelFormatter={(label) => `Heure: ${label}`}
                  />
                  <Legend />

                  {/* Zones de référence colorées */}
                  {/* Zone rouge critique basse (< 0.4 kPa) */}
                  <ReferenceArea y1={0} y2={0.4} fill="#EF4444" fillOpacity={0.1} />

                  {/* Zone jaune acceptable basse (0.4-0.8 kPa) */}
                  <ReferenceArea y1={0.4} y2={0.8} fill="#EAB308" fillOpacity={0.1} />

                  {/* Zone verte optimale (0.8-1.2 kPa) */}
                  <ReferenceArea y1={0.8} y2={1.2} fill="#10B981" fillOpacity={0.15} />

                  {/* Zone jaune acceptable haute (1.2-2.0 kPa) */}
                  <ReferenceArea y1={1.2} y2={2.0} fill="#EAB308" fillOpacity={0.1} />

                  {/* Zone rouge critique haute (> 2.0 kPa) */}
                  <ReferenceArea y1={2.0} y2={10} fill="#EF4444" fillOpacity={0.1} />

                  <Line
                    type="monotone"
                    dataKey="vpd"
                    stroke="#2563EB"
                    strokeWidth={3}
                    name="VPD (kPa)"
                    dot={(props) => {
                      const { cx, cy, payload } = props;
                      const vpd = payload.vpd;
                      let fill = '#10B981'; // Vert par défaut (optimal)

                      if (vpd < 0.4 || vpd > 2.0) {
                        fill = '#EF4444'; // Rouge (critique)
                      } else if ((vpd >= 0.4 && vpd < 0.8) || (vpd > 1.2 && vpd <= 2.0)) {
                        fill = '#EAB308'; // Jaune (acceptable)
                      }

                      return <circle cx={cx} cy={cy} r={4} fill={fill} stroke={fill} strokeWidth={1} />;
                    }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
              <div className="mt-4 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-gray-700">Optimal: 0.8-1.2 kPa</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span className="text-gray-700">Acceptable: 0.4-0.8 ou 1.2-2.0 kPa</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded"></div>
                  <span className="text-gray-700">Critique: &lt;0.4 ou &gt;2.0 kPa</span>
                </div>
              </div>
            </div>
          </div>

          {/* Graphique d'irrigation recommandée - Planification irrigation goutte à goutte */}
          <div className="mt-6">
            <div className="bg-white rounded-lg border border-gray-200 p-6">
              {/* Badge informatif */}
              <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-2">
                  <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                    isCoveredCrop
                      ? 'bg-blue-100 text-blue-800 border border-blue-200'
                      : 'bg-green-100 text-green-800 border border-green-200'
                  }`}>
                    {isCoveredCrop ? '🏠 Culture sous serre - Pluie non déduite' : '🌧️ Plein champ - Pluie déduite'}
                  </div>
                  {cultureName && (
                    <div className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800 border border-gray-200">
                      {cultureName.charAt(0).toUpperCase() + cultureName.slice(1)} (Kc = {cropCoefficient})
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Recommandation d'irrigation - Prochaines 24h
                </h3>
                <div className="relative group">
                  <Info className="w-4 h-4 text-gray-500 cursor-help" />
                  <div className="absolute left-0 top-full mt-2 w-96 bg-gray-900 text-white text-xs rounded-lg p-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10 shadow-lg">
                    <p className="font-semibold mb-2">Calcul de l'irrigation recommandée</p>
                    <p className="mb-2">
                      <strong>Formule :</strong> Irrigation = (ET0 × Kc × FacteurVPD {!isCoveredCrop && '- Pluie'}) / 0.92
                    </p>
                    <p className="mb-2 text-gray-300">
                      • <strong>ET0</strong> : Évapotranspiration de référence<br/>
                      • <strong>Kc</strong> : Coefficient cultural ({cropCoefficient})<br/>
                      • <strong>FacteurVPD</strong> : Ajustement selon stress hydrique<br/>
                      • <strong>0.92</strong> : Efficience système goutte-à-goutte
                    </p>
                    <div className="space-y-1 mt-2 pt-2 border-t border-gray-700">
                      <p><span className="font-semibold text-green-400">Zone verte (0-0.15 mm/h):</span> Irrigation faible</p>
                      <p><span className="font-semibold text-yellow-400">Zone jaune (0.15-0.25 mm/h):</span> Irrigation modérée</p>
                      <p><span className="font-semibold text-orange-400">Zone orange (0.25-0.35 mm/h):</span> Irrigation importante</p>
                      <p><span className="font-semibold text-red-400">Zone rouge (&gt;0.35 mm/h):</span> Irrigation maximale</p>
                    </div>
                  </div>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={prepareHourlyForecastData()}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="heure"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                  />
                  <YAxis
                    label={{ value: 'Irrigation (mm/h)', angle: -90, position: 'insideLeft' }}
                    domain={[0, 'auto']}
                  />
                  <Tooltip
                    formatter={(value) => {
                      let status = '';
                      if (value <= 0.15) status = ' (Faible)';
                      else if (value <= 0.25) status = ' (Modérée)';
                      else if (value <= 0.35) status = ' (Importante)';
                      else status = ' (Maximale)';
                      return [`${value.toFixed(3)} mm/h${status}`, 'Irrigation'];
                    }}
                    labelFormatter={(label) => `Heure: ${label}`}
                  />
                  <Legend />

                  {/* Zones de référence colorées pour l'irrigation */}
                  {/* Zone verte faible (0-0.15 mm/h) */}
                  <ReferenceArea y1={0} y2={0.15} fill="#10B981" fillOpacity={0.15} />

                  {/* Zone jaune modérée (0.15-0.25 mm/h) */}
                  <ReferenceArea y1={0.15} y2={0.25} fill="#EAB308" fillOpacity={0.1} />

                  {/* Zone orange importante (0.25-0.35 mm/h) */}
                  <ReferenceArea y1={0.25} y2={0.35} fill="#F97316" fillOpacity={0.1} />

                  {/* Zone rouge maximale (>0.35 mm/h) */}
                  <ReferenceArea y1={0.35} y2={1.0} fill="#EF4444" fillOpacity={0.1} />

                  <Bar
                    dataKey="irrigation"
                    name="Irrigation (mm/h)"
                    radius={[4, 4, 0, 0]}
                  >
                    {prepareHourlyForecastData().map((entry, index) => {
                      const color = getIrrigationDotColor(entry.irrigation);
                      return <Cell key={`cell-${index}`} fill={color} />;
                    })}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <div className="mt-4 flex flex-wrap items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-green-500 rounded"></div>
                  <span className="text-gray-700">Faible: 0-0.15 mm/h</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-yellow-500 rounded"></div>
                  <span className="text-gray-700">Modérée: 0.15-0.25 mm/h</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-orange-500 rounded"></div>
                  <span className="text-gray-700">Importante: 0.25-0.35 mm/h</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-red-500 rounded"></div>
                  <span className="text-gray-700">Maximale: &gt;0.35 mm/h</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Vue Historique */}
      {selectedView === 'history' && (
        <div className="space-y-6">
          {/* Sélecteur de période */}
          <div className="flex items-center gap-4">
            <Calendar className="w-5 h-5 text-gray-600" />
            <span className="text-gray-700 font-medium">Période:</span>
            <div className="flex gap-2">
              {[7, 30, 90].map((days) => (
                <button
                  key={days}
                  onClick={() => setSelectedPeriod(days)}
                  className={`px-4 py-2 rounded-lg transition ${
                    selectedPeriod === days
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {days} jours
                </button>
              ))}
            </div>
          </div>

          {/* Graphique température */}
          {history && history.length > 0 && (
            <>
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Évolution de la température
                </h3>

                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={prepareHistoryChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="température"
                      stroke="#F59E0B"
                      strokeWidth={2}
                      name="Température (°C)"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Graphique humidité */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Évolution de l'humidité
                </h3>

                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={prepareHistoryChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="humidité"
                      stroke="#3B82F6"
                      strokeWidth={2}
                      name="Humidité (%)"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Graphique précipitations */}
              <div className="bg-white rounded-lg border border-gray-200 p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Précipitations
                </h3>

                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={prepareHistoryChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="time" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="précipitations"
                      fill="#6366F1"
                      name="Précipitations (mm)"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </>
          )}

          {(!history || history.length === 0) && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <div className="flex items-start">
                <AlertTriangle className="w-6 h-6 text-yellow-600 mt-0.5" />
                <div className="ml-3">
                  <h3 className="text-yellow-800 font-semibold">Aucune donnée historique disponible</h3>
                  <p className="text-yellow-700 mt-1">
                    Aucune donnée agrégée quotidienne trouvée pour la période sélectionnée ({selectedPeriod} jours).
                  </p>

                  <div className="mt-3 text-sm text-yellow-700">
                    <p className="font-medium mb-2">Causes possibles:</p>
                    <ul className="list-disc list-inside space-y-1 ml-2">
                      <li>Les données historiques sont collectées lors de l'automation quotidienne (7h00)</li>
                      <li>Le système doit accumuler au moins 1 jour de données avant affichage</li>
                      <li>Vérifier que l'automation est activée et s'exécute correctement</li>
                    </ul>
                  </div>

                  <div className="mt-3 text-sm text-yellow-700">
                    <p className="font-medium mb-1">Pour importer des données historiques:</p>
                    <code className="block bg-yellow-100 p-2 rounded text-xs font-mono">
                      node scripts/import-weather-history.js {farmId} {selectedPeriod} day
                    </code>
                  </div>

                  <button
                    onClick={loadHistoryData}
                    className="mt-4 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition text-sm"
                  >
                    Actualiser
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FarmRoadWeatherPanel;
